import { useTranslation } from "react-i18next";
import {
  DatePicker,
  DatePickerInput,
  TextArea,
  TextInput,
} from "@carbon/react";
import ConfirmDialog from "../../components/carbon/ConfirmDialog/ConfirmDialog";

export interface ConfirmDeliveryFormData {
  receptionDate: string;
  remittanceNumber: string;
  comment: string;
}

export interface ConfirmDeliveryDialogProps {
  open: boolean;
  formData: ConfirmDeliveryFormData;
  onChange: (patch: Partial<ConfirmDeliveryFormData>) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}

const COMMENT_MAX = 255;

/**
 * The buyer-only "Confirm Receipt" dialog. Migrated off the pre-Carbon `Modal` + bare
 * `<form>` onto the shared Carbon `ConfirmDialog` — same shape as `CancelOrderDialog.tsx`
 * (a `ConfirmDialog` whose `body` carries fields). The reception-date input is Carbon's
 * `DatePicker` with the default `Y-m-d` (`yyyy-mm-dd`) format, byte-for-byte what the old
 * native `<input type="date">` produced — so the `POST /deliveries/confirm` payload
 * (`{ token, receptionDate, remittanceNumber, receiverComment }`) stays identical.
 *
 * Deliberately NOT gated with `confirmDisabled`: the pre-Carbon form allowed submitting with
 * empty fields, and that permissive behaviour is preserved rather than tightened here.
 */
const ConfirmDeliveryDialog = ({
  open,
  formData,
  onChange,
  onConfirm,
  onCancel,
  isPending,
}: ConfirmDeliveryDialogProps) => {
  const { t } = useTranslation();

  return (
    <ConfirmDialog
      open={open}
      title={t("deliveries.receiverComment.confirmButton")}
      confirmLabel={t("deliveries.receiverComment.confirmButton")}
      onConfirm={onConfirm}
      onCancel={onCancel}
      isPending={isPending}
      body={
        <div className="delivery-confirm">
          <p className="delivery-confirm__question">
            {t("deliveries.confirmDeliveryQuestion")}
          </p>
          <DatePicker
            datePickerType="single"
            dateFormat="Y-m-d"
            allowInput
            value={formData.receptionDate || undefined}
            onChange={(_dates: Date[], dateStr: string) =>
              onChange({ receptionDate: dateStr })
            }
          >
            <DatePickerInput
              id="receptionDate"
              labelText={t("deliveries.form.receptionDate")}
              placeholder="yyyy-mm-dd"
              onBlur={(event) => {
                const raw = (event.target as HTMLInputElement).value;
                if (raw !== formData.receptionDate) {
                  onChange({ receptionDate: raw });
                }
              }}
            />
          </DatePicker>
          <TextInput
            id="remittanceNumber"
            labelText={t("deliveries.form.remittanceNumber")}
            placeholder={t("deliveries.form.remittanceNumber")}
            maxLength={255}
            value={formData.remittanceNumber}
            onChange={(event) =>
              onChange({ remittanceNumber: event.target.value })
            }
          />
          <div>
            <TextArea
              id="comment"
              labelText={t("deliveries.form.comment")}
              placeholder={t("deliveries.receiverComment.placeholder")}
              rows={4}
              maxLength={COMMENT_MAX}
              value={formData.comment}
              onChange={(event) => {
                if (event.target.value.length >= COMMENT_MAX) return;
                onChange({ comment: event.target.value });
              }}
            />
            <p className="delivery-confirm__counter">
              {formData.comment.length} / {COMMENT_MAX}{" "}
              {t("deliveries.receiverComment.characters")}
            </p>
          </div>
        </div>
      }
    />
  );
};

export default ConfirmDeliveryDialog;
